import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Separator } from '@vaiu/ui/separator';
import { Package, User, MapPin, Phone, Mail, DollarSign, Calendar, Hash } from 'lucide-react';

interface OrderDisplayProps {
  order: any;
  className?: string;
  showHeader?: boolean;
}

/**
 * OrderDisplay Component
 * Displays order information from a token's payload in a structured format
 */
const OrderDisplay: React.FC<OrderDisplayProps> = ({ 
  order, 
  className = '',
  showHeader = true 
}) => {
  // Helper function to render nested objects
  const renderValue = (value: any, depth: number = 0): JSX.Element => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400">—</span>;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <div className={`${depth > 0 ? 'ml-4' : ''} space-y-1`}>
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="flex items-start gap-2">
              <span className="text-sm font-medium text-gray-600 min-w-[120px]">
                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
              </span>
              <div className="flex-1">
                {renderValue(val, depth + 1)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div className={`${depth > 0 ? 'ml-4' : ''} space-y-1`}>
          {value.map((item, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="text-sm text-gray-500">[{index}]</span>
              <div className="flex-1">
                {renderValue(item, depth + 1)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Primitive value
    return <span className="text-sm">{String(value)}</span>;
  };

  // Extract common order fields if they exist
  const orderId = order.id || order.orderId || order.order_id;
  const customer = order.customer || order.buyer || order.user;
  const items = order.items || order.products || order.line_items;
  const total = order.total || order.amount || order.totalAmount;
  const status = order.status || order.orderStatus;
  const createdAt = order.createdAt || order.created_at || order.date;

  return (
    <Card className={className}>
      {showHeader && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Order Details
            </CardTitle>
            {status && (
              <Badge variant={
                status === 'completed' ? 'default' :
                status === 'pending' ? 'secondary' :
                status === 'processing' ? 'outline' :
                'destructive'
              }>
                {status}
              </Badge>
            )}
          </div>
        </CardHeader>
      )}
      
      <CardContent className="space-y-4">
        {/* Order ID */}
        {orderId && (
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">Order ID:</span>
            <span className="text-sm font-mono">{orderId}</span>
          </div>
        )}

        {/* Created Date */}
        {createdAt && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">Date:</span>
            <span className="text-sm">
              {new Date(createdAt).toLocaleString()}
            </span>
          </div>
        )}

        {/* Total Amount */}
        {total && (
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium">Total:</span>
            <span className="text-sm font-semibold">
              {typeof total === 'object' 
                ? `${total.currency || 'USD'} ${total.amount || total.value}` 
                : total}
            </span>
          </div>
        )}

        <Separator />

        {/* Customer Information */}
        {customer && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Customer Information</span>
            </div>
            <div className="ml-6 space-y-1">
              {customer.name && (
                <div className="text-sm">
                  <span className="font-medium">Name:</span> {customer.name}
                </div>
              )}
              {customer.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-3 h-3 text-gray-400" />
                  {customer.email}
                </div>
              )}
              {customer.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-3 h-3 text-gray-400" />
                  {customer.phone}
                </div>
              )}
              {customer.address && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-3 h-3 text-gray-400 mt-1" />
                  <div>
                    {typeof customer.address === 'string' 
                      ? customer.address 
                      : renderValue(customer.address, 0)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Order Items */}
        {items && items.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Order Items</span>
              </div>
              <div className="ml-6 space-y-2">
                {items.map((item: any, index: number) => (
                  <div key={index} className="p-2 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {item.name || item.title || item.product_name || `Item ${index + 1}`}
                        </div>
                        {item.description && (
                          <div className="text-xs text-gray-600 mt-1">
                            {item.description}
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        {item.quantity && (
                          <div className="text-sm">Qty: {item.quantity}</div>
                        )}
                        {item.price && (
                          <div className="text-sm font-medium">
                            {typeof item.price === 'object' 
                              ? `${item.price.currency || ''} ${item.price.amount || item.price.value}`
                              : item.price}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Raw JSON Display for all fields */}
        <Separator />
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-600">Complete Order Data</div>
          <div className="p-3 bg-gray-50 rounded-lg overflow-x-auto">
            <div className="text-xs font-mono">
              {renderValue(order)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderDisplay;
